import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import Pricing from "../showPrice/Pricing"
import {
  Loader2,
  Eye,
  RefreshCw,
  X,
  BarChart3,
} from "lucide-react";
import { useAPI } from "../Context/MyContext";
import CampaignModal from "../Component/braodcasting/CampaignModal";
import CreditBadge from "../Component/CreditBadge";

const ITEMS_PER_PAGE = 10;

const Broadcasting = () => {
  const { register, watch } = useForm();
  const searchQuery = watch("search", "");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState(null);
  const { fetchCampaigns, getCampaignDetails } = useAPI();
  const [showModal, setShowModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Main function to load campaigns
  const loadCampaigns = async (page, search, status) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCampaigns(page, search, status);
      setCampaigns(data.campaigns);
      setTotalPages(data.pagination.total_pages);
      setTotalRecords(data.pagination.total_records);
    } catch (err) {
      setError(err.message);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when filters change
  useEffect(() => {
    loadCampaigns(currentPage, searchQuery, statusFilter);
  }, [currentPage, statusFilter]);

  // Handle search with debouncing
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1); // Reset to page 1 on search
      loadCampaigns(1, searchQuery, statusFilter);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleViewCampaign = async (campaignId) => {
    setShowModal(true);
    setModalLoading(true);
    setModalError(null);

    try {
      const res = await getCampaignDetails(campaignId);
      setSelectedCampaign(res);
    } catch (err) {
      setModalError(err.message);
    } finally {
      setModalLoading(false);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to page 1
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    loadCampaigns(currentPage, searchQuery, statusFilter);
  };

  // Format date
  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // Status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "DRAFT":
        return "bg-gray-100 text-gray-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Generate pagination numbers
  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
         <CreditBadge/>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h2 className="text-2xl font-semibold text-gray-800">
              Broadcasting
            </h2>
            <Link
              to="/sendbulk"
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm font-medium shadow transition"
            >
              + Create Campaign
            </Link>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
             {/* <CreditBadge/> */}
          </div>

          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <input
              {...register("search")}
              type="text"
              placeholder="Search campaigns..."
              className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

     
          {/* Available Credits */}
          {/* <div className="mb-4 text-sm text-gray-600">
            Available Credits: <span className="font-medium">1000</span>
          </div> */}
         

        
        {/* Stats Summary */}
        {totalRecords > 0 && (
          <div className="mb-6 text-sm text-gray-600">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
            {Math.min(currentPage * ITEMS_PER_PAGE, totalRecords)} of {totalRecords} campaigns
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">Error: {error}</p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-600 font-semibold">
                <tr>
                  <th className="px-4 py-3">Campaign Name</th>
                  <th className="px-4 py-3">Template</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Failed</th>
                  <th className="px-4 py-3">Pending</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="py-8 text-center text-gray-500">
                      <div className="flex justify-center items-center gap-2">
                        <Loader2 className="animate-spin w-5 h-5 text-blue-500" />
                        Loading campaigns...
                      </div>
                    </td>
                  </tr>
                ) : campaigns.length > 0 ? (
                  campaigns.map((campaign) => (
                    <tr
                      key={campaign._id}
                      className="hover:bg-gray-50 border-t border-gray-100 transition"
                    >
                      <td className="px-4 py-3 font-medium">
                        {campaign.campaign_name}
                      </td>
                      <td className="px-4 py-3">{campaign.template_name}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            campaign.status
                          )}`}
                        >
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">{campaign.total_contacts}</td>
                      <td className="px-4 py-3 text-green-600">
                        {campaign.sent || 0}
                      </td>
                      <td className="px-4 py-3 text-red-600">
                        {campaign.failed || 0}
                      </td>
                      <td className="px-4 py-3 text-yellow-600">
                        {campaign.pending || 0}
                      </td>
                      <td className="px-4 py-3">
                        {formatDate(campaign.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewCampaign(campaign._id)}
                            className="p-1 text-blue-600 hover:text-blue-800 transition"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* <Link
                            to={`/campaigns/${campaign._id}/stats`}
                            className="p-1 text-green-600 hover:text-green-800 transition"
                            title="View Stats"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Link> */}

                          {/* {campaign.failed > 0 && (
                            <button
                              className="p-1 text-orange-600 hover:text-orange-800 transition"
                              title="Retry Failed"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )} */}

                          {/* {["PENDING", "IN_PROGRESS"].includes(
                            campaign.status
                          ) && (
                              <button
                                className="p-1 text-red-600 hover:text-red-800 transition"
                                title="Cancel Campaign"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )} */}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="py-8 text-center text-gray-500">
                      No campaigns found.
                      {searchQuery && " Try adjusting your search terms."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-md border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-blue-500 hover:text-white hover:border-blue-500 transition"
              >
                Previous
              </button>

              {getPaginationRange().map((pageNum, index) => {
                if (pageNum === '...') {
                  return (
                    <span
                      key={`dots-${index}`}
                      className="px-4 py-2 text-gray-500"
                    >
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-4 py-2 rounded-md border text-sm font-medium transition ${currentPage === pageNum
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-blue-500 hover:text-white hover:border-blue-500"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-md border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-blue-500 hover:text-white hover:border-blue-500 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {showModal && (
          <CampaignModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            campaignData={selectedCampaign}
            loading={modalLoading}
            error={modalError}
          />
        )}
        {/* <Pricing /> */}
      </div>
    </div>
  );
};

export default Broadcasting;